import React from 'react';
import { cn } from 'lib/utils';
import { Check } from 'lucide-react';

export interface QuestionOption {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
}

interface OnboardingQuestionProps {
  title: string;
  description?: string;
  options: QuestionOption[];
  selectedIds: string[];
  onSelect: (id: string) => void;
  multiple?: boolean;
}

export const OnboardingQuestion: React.FC<OnboardingQuestionProps> = ({
  title,
  description,
  options,
  selectedIds,
  onSelect,
  multiple = false
}) => {
  const handleOptionClick = (id: string) => {
    onSelect(id);
  };

  return (
    <div className="flex flex-col gap-5 max-md:gap-4">
      <div>
        <h2 className="text-xl font-semibold text-foreground-strong m-0 leading-tight">
          {title}
        </h2>
        {description && (
          <p className="text-sm text-foreground-muted mt-2 mb-0 leading-relaxed max-[480px]:text-sm">
            {description}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-3 mt-2 max-md:gap-2.5">
        {options.map(option => {
          const isSelected = selectedIds.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              className={cn(
                'flex items-center gap-3.5 p-4 rounded-xl border cursor-pointer transition-all text-left text-sm w-full hover:border-primary/40 hover:shadow-lg active:translate-y-0 max-md:p-3.5 max-md:gap-3 max-[480px]:p-3 max-[480px]:gap-2.5 max-[480px]:text-sm',
                isSelected
                  ? 'border-primary/40 bg-primary/10 font-medium'
                  : 'border-slate-500/15 bg-card font-normal'
              )}
              onClick={() => handleOptionClick(option.id)}
            >
              <div
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-xl shrink-0 transition-all max-[480px]:w-9 max-[480px]:h-9',
                  isSelected
                    ? 'bg-primary text-white'
                    : 'bg-muted text-foreground-muted'
                )}
              >
                {option.icon}
              </div>
              <div className="flex-1 flex flex-col gap-1 min-w-0">
                <span className="font-medium text-foreground-strong leading-tight max-[480px]:text-sm">
                  {option.label}
                </span>
                {option.description && (
                  <span className="text-sm text-foreground-muted leading-snug line-clamp-2 max-[480px]:text-xs">
                    {option.description}
                  </span>
                )}
              </div>
              {multiple && (
                <div
                  className={cn(
                    'flex items-center justify-center w-6 h-6 rounded-full shrink-0 transition-all max-[480px]:w-[22px] max-[480px]:h-[22px]',
                    isSelected
                      ? 'bg-primary border-transparent'
                      : 'bg-transparent border border-slate-500/20'
                  )}
                >
                  {isSelected && <Check size={12} color="white" />}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
